/**
 * Per-module sidebar flags owned by the super admin console — which modules a
 * role can see, and which of them wear a "New" pill.
 *
 * Each map is fetched once per signed-in session and shared by all three
 * sidebars — they mount and unmount as you navigate between shells, and a fetch
 * per mount would put a request on every navigation for a value that changes
 * about once a month.
 *
 * The two maps fail open in OPPOSITE directions, which is the point of the
 * per-store fallback:
 *
 * * Visibility defaults to VISIBLE while loading and on failure. Defaulting to
 *   hidden would blank the sidebar on every cold load and lock people out of the
 *   app whenever this endpoint had a bad minute — the failure mode has to be
 *   "shows a module that should be hidden", never "shows nothing at all".
 * * New tags default to UNTAGGED. A missing pill is invisible; a spurious one
 *   flashing onto every entry during load would be noise on every navigation.
 */
import { useCallback, useEffect, useState } from "react";

import { api } from "@/lib/api";

export type ModuleMap = Record<string, boolean>;

/** One cached, shared, fail-soft boolean map behind a `(key?) => boolean` hook. */
function createFlagStore(path: string, fallback: boolean) {
  let cache: ModuleMap | null = null;
  let inflight: Promise<ModuleMap> | null = null;
  const subscribers = new Set<(m: ModuleMap) => void>();

  function load(): Promise<ModuleMap> {
    if (cache) return Promise.resolve(cache);
    if (!inflight) {
      inflight = api
        .get<ModuleMap>(path)
        .catch(() => ({}) as ModuleMap) // fail soft — see the file comment
        .then((m) => {
          cache = m;
          inflight = null;
          subscribers.forEach((fn) => fn(m));
          return m;
        });
    }
    return inflight;
  }

  function reset(): void {
    cache = null;
    inflight = null;
  }

  /** The shared map, or null until the first fetch settles (success or not). */
  function useMap(): ModuleMap | null {
    const [map, setMap] = useState<ModuleMap | null>(cache);

    useEffect(() => {
      let stale = false;
      const onChange = (m: ModuleMap) => {
        if (!stale) setMap(m);
      };
      subscribers.add(onChange);
      void load().then(onChange);
      return () => {
        stale = true;
        subscribers.delete(onChange);
      };
    }, []);

    return map;
  }

  function useFlag(): (key?: string) => boolean {
    const map = useMap();
    return (key?: string) => {
      // No key = not a registered module; still loading = don't flicker. Either
      // way the caller gets this store's safe direction.
      if (!key || !map) return fallback;
      const value = map[key];
      return typeof value === "boolean" ? value : fallback;
    };
  }

  return { reset, useFlag, useMap };
}

const visibility = createFlagStore("/lookups/module-visibility", true);
const newTags = createFlagStore("/lookups/module-new-tags", false);

/** Drop the cached maps — call on sign-out so the next user fetches their own. */
export function resetModuleVisibility(): void {
  visibility.reset();
  newTags.reset();
}

/**
 * Modules held back in code, on top of the super admin's switchboard.
 *
 * A temporary release gate, NOT a deletion: a module listed here ships in the
 * build (routes, pages, authoring and API untouched) but is not advertised
 * anywhere in the product until it is cleared to launch. Removing its key
 * brings it back everywhere at once. BL Academy is held here again: it went
 * out with Release 8 before it was cleared, so it is pulled from buyers, BL
 * advisors and admins until launch.
 *
 * Why here and not the super-admin Module Visibility switch: that switch is
 * DB-backed and fails OPEN (see the file comment above), so a bad minute on
 * that endpoint would put the module back in front of users. A release gate has
 * to hold even when the flag fetch fails.
 *
 * Why in this module rather than in the sidebar, which is where it started: the
 * sidebar is not the only surface that advertises a module. The buyer dashboard
 * carries a card and a quick link per module too, and gating only the nav left
 * BL Academy hidden from the menu but one dashboard card away from an empty
 * page. Every surface already asks this hook whether a module is visible, so
 * the gate belongs on the answer.
 */
const RELEASE_GATED = new Set<string>(["bl_academy"]);

/**
 * `isVisible(key)` for the signed-in viewer. An unknown key returns true: a
 * nav item whose module was never registered is not something to hide. A
 * release-gated key returns false whatever the switchboard says.
 */
export function useModuleVisibility(): (key?: string) => boolean {
  const isVisible = visibility.useFlag();
  return useCallback(
    (key?: string) => !RELEASE_GATED.has(key ?? "") && isVisible(key),
    [isVisible],
  );
}

/**
 * Whether the visibility map has settled. Route guards wait on this so a
 * switched-off page redirects instead of flashing its content first. A failed
 * fetch still counts as settled — and, failing open, shows everything.
 */
export function useModuleVisibilityReady(): boolean {
  return visibility.useMap() !== null;
}

/**
 * `isNew(key)` — whether the super admin has tagged this module New. An unknown
 * or unregistered key returns false, so only a deliberate toggle shows a pill.
 */
export const useModuleNewTag = newTags.useFlag;
